import { Directive, inject } from '@angular/core';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { Content, ContentTypeEnum, IPagination } from 'lingo2-models';
import { isArray as _isArray } from 'lodash';
import { takeUntil, filter } from 'rxjs/operators';
import { PaneDataLoaderService } from '../../../_v3/services/pane-data-loader.service';
import { SrvDefaults } from '../../../_v3/services/srvdefaults.config';
import { AbstractPane } from './abstract-pane';

@Directive()
// tslint:disable-next-line:directive-class-suffix
export abstract class AbstractContentPane extends AbstractPane {
  protected _TAG = `[----] ${this.constructor.name}:`;
  protected readonly store = inject(Store);
  protected readonly paneLoader = inject(PaneDataLoaderService);

  public contentRoute = LibraryRouter.contentRouteUniversal;
  protected libRouter: LibraryRouter = new LibraryRouter({});

  protected filter_libarayRoute = (data: any) => true;

  constructor() {
    super();

    this.store
      .select(getLibraryRouteData)
      .pipe(
        filter((data) => this.filter_libarayRoute(data)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (data) => {
          if (!data) return;
          this.libRouter.setBaseRoute(data.baseRoute);
          this.libRouter.setSubjects(data.subjectCodes);
          this.libRouter.setContentTypes(data.contentTypeCodes);
          if (!data.isDefaultOnlyLanguages) {
            this.libRouter.setLanguageCodes(data.languageCodes);
          }
          this.onLibraryRoute();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get content(): Content[] {
    if (!this._pane) return [];
    return this._pane.contentItems || this._pane.content || [];
  }

  override hasContent(): boolean {
    return !!(this.content && this.content.length);
  }

  // No override load!
  public load(): void {
    if (!this._pane) return;

    this.onLibraryRoute();
    if (this._pane.contentFilter) {
      if (this._pane.loading) return;
      this.clear();
      this._pane.filter = { ...this._pane.contentFilter };
      super.loadPane();
      return;
    }
    super.loadPane();
  }

  protected onLibraryRoute(): void {
    if (this._pane) {
      this.addLink = this._pane.routerUrlCreate ?? this.libRouter.getCreateLinkByPane(this._pane);
      this.seeAllLink = this._pane.routerUrlAll ?? this.libRouter.getSeeAllLinkByPane(this._pane);
      this.titleLink = this.seeAllLink;
    } else {
      this.addLink = '';
      this.seeAllLink = '';
      this.titleLink = '';
    }

    this.blockTitle.add_link = this.addLink;
    this.blockTitle.see_all_link = this._pane?.infinity ? '' : this.seeAllLink;
    this.blockTitle.title_link = this.titleLink;

    this.markForCheck();
  }

  protected override calculateTitle(): string | string[] {
    if (this._pane?.title) {
      if (this._pane.title === 'none') return '';
      return this._pane.title;
    }

    if (this._pane?.contentFilter?.content_type) {
      const contentType = this._pane.contentFilter.content_type;
      if (_isArray(contentType)) {
        return contentType.map((ct) => `content-types-title.${ContentTypeEnum[ct]}`);
      } else {
        return `content-types-title.${ContentTypeEnum[contentType]}`;
      }
    }

    return super.calculateTitle();
  }

  protected clear() {
    this._pane.contentItems = [];
    this._pane.pagination = undefined;
    this._loadedItemsCount = 0;
    this._pane.loading = true;
    this._pane.loaded = false;
  }

  protected loadContentInternal(): void {
    this._pane.loading = true;
    console.debug(`${this._TAG} loadContentInternal for pane:`, this._pane);

    const filter = this._pane.contentFilter;
    const method = filter?.related_to ? 'getRelated' : 'getContent';
    const args = filter?.related_to ? [[filter.related_to, filter]] : filter;

    this.paneLoader
      .getDataFromService(this._pane, { ...SrvDefaults.ServicesMap.content, method }, args, this.destroyed$)
      .subscribe({
        next: (res: any) => {
          console.debug(`❤️❤️❤️❤️`, this._pane, res);
          this._loadedItemsCount = this.content.length;
          this.loadDataFinished(true);
        },
        error: (err) => {
          console.warn(`[AbstractContentPane] loadContent error`, err);
          this.loadDataFinished(false);
        },
      });
  }
}
